/**
 * Permissions matrix for the 8-level role hierarchy
 * Each permission maps to the minimum role required
 */
import { UserRole, hasMinimumRole } from '@/types/roles';

// All platform permissions
export const PERMISSIONS = {
  // Candidate+ permissions
  canViewJobs: UserRole.CANDIDATE,
  canApplyToJobs: UserRole.CANDIDATE,
  canManageOwnProfile: UserRole.CANDIDATE,
  canTrackApplications: UserRole.CANDIDATE,
  canMessageRecruiters: UserRole.CANDIDATE,

  // Freelancer+ permissions
  canAcceptMissions: UserRole.FREELANCER,
  canViewPlanning: UserRole.FREELANCER,
  canTrackPayments: UserRole.FREELANCER,
  canViewCertifications: UserRole.FREELANCER,
  canViewEvaluations: UserRole.FREELANCER,

  // Employer+ permissions
  canPostJobs: UserRole.EMPLOYER,
  canViewApplications: UserRole.EMPLOYER,
  canMessageCandidates: UserRole.EMPLOYER,
  canViewRecruitmentStats: UserRole.EMPLOYER,
  canManageBilling: UserRole.EMPLOYER,

  // Manager+ permissions
  canManageTeam: UserRole.MANAGER,
  canAssignMissions: UserRole.MANAGER,
  canViewTeamPerformance: UserRole.MANAGER,
  canViewTeamReports: UserRole.MANAGER,
  canApproveLeave: UserRole.MANAGER,

  // HR Manager+ permissions
  canManageRecruitment: UserRole.HR_MANAGER,
  canManageContracts: UserRole.HR_MANAGER,
  canManagePayroll: UserRole.HR_MANAGER,
  canManageOnboarding: UserRole.HR_MANAGER,
  canManageTraining: UserRole.HR_MANAGER,
  canViewComplianceData: UserRole.HR_MANAGER,

  // Department Head+ permissions
  canManageBudget: UserRole.DEPARTMENT_HEAD,
  canSetObjectives: UserRole.DEPARTMENT_HEAD,
  canApproveRecruitment: UserRole.DEPARTMENT_HEAD,
  canViewAdvancedReports: UserRole.DEPARTMENT_HEAD,
  canManageMultiTeams: UserRole.DEPARTMENT_HEAD,

  // Admin+ permissions
  canModerateContent: UserRole.ADMIN,
  canManageUsers: UserRole.ADMIN,
  canConfigurePlatform: UserRole.ADMIN,
  canViewSecurityAlerts: UserRole.ADMIN,
  canViewGlobalReports: UserRole.ADMIN,

  // Super Admin only
  canManageAdmins: UserRole.SUPER_ADMIN,
  canAccessAuditLogs: UserRole.SUPER_ADMIN,
  canManageSystem: UserRole.SUPER_ADMIN,
  canManageRoles: UserRole.SUPER_ADMIN,
  canAccessDatabase: UserRole.SUPER_ADMIN,
} as const;

export type Permission = keyof typeof PERMISSIONS;

/** Check if a user role has a specific permission */
export function hasPermission(userRole: UserRole, permission: Permission): boolean {
  const requiredRole = PERMISSIONS[permission];
  return hasMinimumRole(userRole, requiredRole);
}

/** Check if a user role has ALL specified permissions */
export function hasAllPermissions(userRole: UserRole, permissions: Permission[]): boolean {
  return permissions.every(p => hasPermission(userRole, p));
}

/** Check if a user role has ANY of the specified permissions */
export function hasAnyPermission(userRole: UserRole, permissions: Permission[]): boolean {
  return permissions.some(p => hasPermission(userRole, p));
}

/** Get all permissions for a role */
export function getPermissionsForRole(userRole: UserRole): Permission[] {
  return (Object.keys(PERMISSIONS) as Permission[]).filter(p => hasPermission(userRole, p));
}

/** Navigation items per role */
export const ROLE_NAV_ITEMS: Record<UserRole, Array<{ key: string; path: string; icon: string }>> = {
  [UserRole.CANDIDATE]: [
    { key: 'jobs', path: '/careers/jobs', icon: 'Briefcase' },
    { key: 'applications', path: '/candidate/applications', icon: 'FileText' },
    { key: 'profile', path: '/candidate/profile', icon: 'User' },
  ],
  [UserRole.FREELANCER]: [
    { key: 'missions', path: '/freelancer/missions', icon: 'Target' },
    { key: 'planning', path: '/freelancer/planning', icon: 'Calendar' },
    { key: 'payments', path: '/freelancer/payments', icon: 'CreditCard' },
    { key: 'training', path: '/freelancer/training', icon: 'GraduationCap' },
  ],
  [UserRole.EMPLOYER]: [
    { key: 'dashboard', path: '/employer/dashboard', icon: 'LayoutDashboard' },
    { key: 'postJob', path: '/employer/post-job', icon: 'PlusCircle' },
    { key: 'applications', path: '/employer/applications', icon: 'Users' },
    { key: 'profiles', path: '/employer/profiles', icon: 'Search' },
  ],
  [UserRole.MANAGER]: [
    { key: 'team', path: '/manager/team', icon: 'Users' },
    { key: 'missions', path: '/manager/missions', icon: 'Target' },
    { key: 'performance', path: '/manager/performance', icon: 'TrendingUp' },
    { key: 'reports', path: '/manager/reports', icon: 'BarChart' },
  ],
  [UserRole.HR_MANAGER]: [
    { key: 'recruitment', path: '/hr/recruitment', icon: 'UserPlus' },
    { key: 'employees', path: '/hr/employees', icon: 'Users' },
    { key: 'training', path: '/hr/training', icon: 'GraduationCap' },
    { key: 'compliance', path: '/hr/compliance', icon: 'Shield' },
  ],
  [UserRole.DEPARTMENT_HEAD]: [
    { key: 'overview', path: '/director/dashboard', icon: 'LayoutDashboard' },
    { key: 'budget', path: '/director/budget', icon: 'Wallet' },
    { key: 'objectives', path: '/director/objectives', icon: 'Target' },
    { key: 'reports', path: '/director/reports', icon: 'BarChart' },
  ],
  [UserRole.ADMIN]: [
    { key: 'moderation', path: '/admin/moderation', icon: 'Shield' },
    { key: 'users', path: '/admin/users', icon: 'Users' },
    { key: 'settings', path: '/admin/settings', icon: 'Settings' },
    { key: 'alerts', path: '/admin/alerts', icon: 'Bell' },
  ],
  [UserRole.SUPER_ADMIN]: [
    { key: 'system', path: '/admin/system', icon: 'Server' },
    { key: 'audit', path: '/admin/audit-logs', icon: 'ScrollText' },
    { key: 'database', path: '/admin/database', icon: 'Database' },
    { key: 'roles', path: '/admin/rbac', icon: 'Key' },
  ],
};
